import { Autocomplete, Box, Button, CircularProgress, Stack, TextField } from '@mui/material'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import Header from 'components/Header'
import { useBasePath } from 'components/Providers'
import PersonSearch from 'components/PersonSearch'
import { save, search, destroy } from 'store/mentorship/mentorship'
import RequiredLabel from 'components/RequiredLabel'
import * as snackbar from 'utils/snackbar'

const schema = yup.object().shape({
  mentee: yup.object().nullable().required('Required'),
  mentor: yup.object().nullable().required('Required'),
  subMentor: yup.object().nullable(),
  startDate: yup.string().required('Required'),
  note: yup.string().nullable(),
})

export default function EnrollmentForm({ item }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const basePath = useBasePath()
  const mentors = useSelector(state => state.mentorship.mentorship.mentors)

  const { control, handleSubmit, setValue, formState } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ...item
    },
  })

  useEffect(() => {
    if (item?.mentorId && mentors) {
      const found = mentors.find(m => m.personId === item.mentorId)
      if (found) setValue('mentor', found)
    }
  }, [item?.mentorId, mentors])

  const handleDelete = async () => {
    await dispatch(destroy({ id: item.id }))
    navigate(basePath)
  }

  const onSubmit = async data => {

    const payload = { 
      
      ...(!item?.id && { menteeId: data.mentee?.id }),
      mentorId: data.mentor?.personId,
      subMentorId: data.subMentor?.id ?? null,
      startDate: data.startDate,
      endDate: data.endDate ? data.endDate : null,
      notes: data.notes,
      ...(item?.id && { id: item.id })
    }

    const result = await dispatch(save(payload))
    if (result.payload) {
      await dispatch(search())
      navigate(basePath)
      return
    }

    const errText = result.error?.data?.error || result.error?.message || ''
    snackbar.error(
      result.error?.status === 409 || /mentee/i.test(errText)
        ? t('This mentee already has a mentorship enrollment.')
        : t('Unable to save enrollment. Please try again.'),
    )
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Header
        title={item?.id ? t('Edit Enrollment') : t('New Enrollment')}
        small
        close={() => navigate(basePath)}
        borderBottom
      />
      <Stack spacing={1} sx={{ p: 2 }}>
        <Controller
          name="mentee"
          control={control}
          render={({ field, fieldState }) => (
            <PersonSearch
              label={<RequiredLabel>{t('Mentee')}</RequiredLabel>}
              value={field.value}
              onChange={field.onChange}
              disabled={!!item?.id}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="mentor"
          control={control}
          render={({ field, fieldState }) => (
            <Autocomplete
              value={mentors?.find(m => m.personId === field.value?.personId) ?? null}
              options={mentors || []}
              getOptionLabel={m => `${m.firstName} ${m.lastName}`}
              isOptionEqualToValue={(option, value) => option.personId === value?.personId}
              onChange={(_, newValue) => field.onChange(newValue)}
              renderInput={params => (
                <TextField
                  {...params}
                  label={<RequiredLabel>{t('Mentor')}</RequiredLabel>}
                  size="small"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}
        />

        <Controller
          name="subMentor"
          control={control}
          render={({ field, fieldState }) => (
            <PersonSearch
              label={t('Sub-Mentor')}
              value={field.value}
              onChange={field.onChange}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="startDate"
          control={control}
          render={({ field, fieldState }) => (
            <DatePicker
              sx={fieldState.error ? { label: { color: 'red' } } : {}}
              slotProps={{ actionBar: { actions: ['clear'] }, textField: { size: 'small' }, field: { fullWidth: true } }}
              label={<RequiredLabel>{t('Start Date')}</RequiredLabel>}
              {...field}
              value={field.value ? dayjs(field.value) : null}
              onChange={date => field.onChange(date ? date.format('YYYY-MM-DD') : '')}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="endDate"
          control={control}
          render={({ field }) => (
            <DatePicker
              slotProps={{ actionBar: { actions: ['clear'] }, textField: { size: 'small', helperText: t('Leave blank if still active') }, field: { fullWidth: true } }}
              label={t('End Date')}
              {...field}
              value={field.value ? dayjs(field.value) : null}
              onChange={date => field.onChange(date ? date.format('YYYY-MM-DD') : '')}
            />
          )}
        />
        <Controller
          name="notes"
          control={control}
          render={({ field }) => (
            <TextField
              size="small"
              label={t('Note')}
              fullWidth
              multiline
              rows={4}
              {...field}
            />
          )}
        />

        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          {item?.id && (
            <Button size="small" variant="outlined" color="error" onClick={handleDelete} disabled={formState.isSubmitting}>
              {t('Delete')}
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            size='small'
            disabled={formState.isSubmitting}
            startIcon={formState.isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {t('Save')}
          </Button>
        </Box>
      </Stack>
    </Box>
  )
}
